import { useState } from 'react';

interface StockLogoProps {
  symbol: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES: Record<string, { css: string; px: number }> = {
  xs: { css: 'text-[8px]', px: 20 },
  sm: { css: 'text-[10px]', px: 28 },
  md: { css: 'text-xs', px: 36 },
  lg: { css: 'text-sm', px: 48 },
};

const SECTOR_COLORS: Record<string, string> = {
  BBCA: '#003d79', BBRI: '#00529c', BMRI: '#003b71', BBNI: '#f05a22', BBTN: '#1a4789',
  BRIS: '#00a550', ARTO: '#6c3caf', BJTM: '#1b3c73', BJBR: '#1b5faa', BTPS: '#e6a817',
  PNBN: '#0066b3', TLKM: '#e4002b', ISAT: '#ed1c24', EXCL: '#461e7d', TOWR: '#1a73b5',
  ASII: '#cc0000', UNTR: '#f7a800', GOTO: '#00aa13', BUKA: '#e31e52', EMTK: '#1a237e',
  MDIA: '#ff6600', MNCN: '#003399', SCMA: '#1565c0', VIVA: '#e53935',
};

function getLetterColor(symbol: string): string {
  if (!symbol) return 'hsl(210, 20%, 50%)';
  if (SECTOR_COLORS[symbol]) return SECTOR_COLORS[symbol];
  let hash = 0;
  for (let i = 0; i < symbol.length; i++) hash = symbol.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 40%)`;
}

export default function StockLogo({ symbol, size = 'sm', className = '' }: StockLogoProps) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const { css: textClass, px } = SIZES[size];
  const safeSymbol = symbol || '??';
  const bgColor = getLetterColor(safeSymbol);
  const logoUrl = `https://assets.stockbit.com/logos/companies/${safeSymbol}.png`;

  return (
    <div
      className={`relative rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-white ${textClass} ${className}`}
      style={{ width: px, height: px, minWidth: px, minHeight: px, backgroundColor: bgColor }}
    >
      {/* Letter fallback — always visible until image loads */}
      {!imgLoaded && safeSymbol.slice(0, 2)}

      {/* Image overlay — hidden until loaded, hidden if failed */}
      {!imgFailed && (
        <img
          src={logoUrl}
          alt={safeSymbol}
          width={px}
          height={px}
          className="absolute inset-0 rounded-lg object-contain bg-white"
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgFailed(true)}
          style={imgLoaded ? undefined : { opacity: 0 }}
        />
      )}
    </div>
  );
}
